import type {
	PortalClientDto,
	PortalClientRelationshipGroup,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';

/**
 * PD-14189 — single-select account/location picker for the storefront list
 * pages (Invoices, Orders, Transactions, Statements). The selection is still
 * single-value (the `clientId` query param is unchanged); the only change is
 * presentational: options are stacked into three alphabetized <optgroup>s —
 * Direct (your accounts) / Children / Ownership — driven by the
 * `relationshipGroup` the backend assigns to each accessible account.
 *
 * Dumb/presentational by design: it does not read any store — the parent page
 * passes `clients` (already brand-scoped) and owns the change handler.
 */
interface AccountLocationPickerProps {
	value: string;
	onChange: (value: string) => void;
	clients: Array<PortalClientDto>;
	className?: string;
	label?: string;
}

export function AccountLocationPicker({
	value,
	onChange,
	clients,
	className,
	label,
}: AccountLocationPickerProps): JSX.Element {
	const { t } = useTranslation();

	// PD-14189: split into the three relationship groups, each sorted by name.
	const groupOf = (
		group: PortalClientRelationshipGroup,
	): Array<PortalClientDto> =>
		clients
			.filter((client) => client.relationshipGroup === group)
			.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

	const directClients = groupOf('direct');
	const childClients = groupOf('child');
	const ownershipClients = groupOf('ownership');

	const selectClassName =
		className != null && className.length > 0
			? `px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm ${className}`
			: 'px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm';

	const renderOptions = (group: Array<PortalClientDto>): Array<JSX.Element> =>
		group.map((client) => (
			<option key={client.id} value={String(client.id)}>
				{t(client.name ?? '')}
			</option>
		));

	return (
		<div>
			<label className="block text-xs font-medium text-brand-muted mb-1">
				{t(label ?? 'Location')}
			</label>
			<select
				value={value}
				onChange={(e) => {
					onChange(e.target.value);
				}}
				className={selectClassName}
			>
				<option value="">{t('All Locations')}</option>
				{directClients.length > 0 && (
					<optgroup label={t('Your Accounts')}>
						{renderOptions(directClients)}
					</optgroup>
				)}
				{childClients.length > 0 && (
					<optgroup label={t('Child Accounts')}>
						{renderOptions(childClients)}
					</optgroup>
				)}
				{ownershipClients.length > 0 && (
					<optgroup label={t('Ownership Accounts')}>
						{renderOptions(ownershipClients)}
					</optgroup>
				)}
			</select>
		</div>
	);
}
